import { z } from 'zod';

/** `profile_questions.key`'s own column width — a wider value could never name a question. */
const QuestionKeySchema = z.string().min(1).max(64);

/**
 * Which two questions hold a member's stated age preference (P16b).
 *
 * ## Why this is a setting and not two seeded questions
 *
 * `02-build.md` asks the nomination score for "age-pref fit +20", and there is
 * **no age-preference question in the tree**: the seeder is deliberately tiny —
 * `country` and `civil_registry_id`, the two `00-full-system.md` names — because
 * seeding a profile is shipping the hardcoded product the question engine exists
 * to replace (A4). Adding two more seeded questions would change every
 * deployment's profile flow, which is a product decision and not this phase's.
 *
 * So the preference lives **inside the question engine**, as two ordinary
 * `number` questions a market creates if it wants them, and this setting is how
 * the scorer learns what they are called. A market that asks its members for a
 * range names them here; a market that does not, does not, and the term simply
 * never contributes.
 *
 * ## The three states, and why the middle one is not the last one
 *
 * The distinction P14a insists on — *a signal that cannot be computed must be
 * `null`, never a weaker guess* — lands here as three genuinely different facts:
 *
 * 1. **The questions do not exist** (or are inactive, or are not `number`
 *    questions, or this setting names one question twice). The term **cannot be
 *    computed**: the stored breakdown records `agePreference: null`, and the
 *    score carries no age component at all.
 * 2. **The questions exist and a member left them blank.** That member states
 *    **no preference**, which fits everybody. This is a computed answer — a
 *    constraint that excludes nobody — and it is the full weight, never `null`.
 *    Reading it as "cannot compute" would punish exactly the members who are
 *    open to anyone.
 * 3. **Both stated a range.** The term is awarded when the fit is **mutual**:
 *    the viewer's range contains the candidate's age *and* the candidate's
 *    contains the viewer's. A one-directional fit is not a match — it is one
 *    person's hope.
 *
 * ## Why `minKey === maxKey` is not refused here
 *
 * It is a misconfiguration, and the obvious home for the refusal is a `.refine`
 * on this schema. It cannot live here: an object-valued setting resolves by
 * **merging layers per field**, so `storedLayerSchema` calls `.partial()` on the
 * definition's schema — and Zod refuses `.partial()` outright on an object
 * carrying a refinement (*".partial() cannot be used on object schemas
 * containing refinements"*). The refusal would therefore not guard a bad write;
 * it would make **every** gender-scoped write throw.
 *
 * So the scorer treats one key named twice as case 1 above — uncomputable,
 * recorded as `null` — which is the same degradation as a question that does not
 * exist, and for the same reason: a settings typo must never take the nomination
 * batch down.
 */
export const NominationAgePreferenceQuestionsSchema = z
  .object({
    /** The question holding the youngest age this member will consider. */
    minKey: QuestionKeySchema,
    /** The question holding the oldest. */
    maxKey: QuestionKeySchema,
  })
  .strict();

export type NominationAgePreferenceQuestions = z.infer<
  typeof NominationAgePreferenceQuestionsSchema
>;

/**
 * Whether this setting can name a range at all — the **first** of the scorer's
 * three states, decided in one place so the repository and its spec agree.
 *
 * Exported rather than inlined because it is the boundary between "compute the
 * term" and "record that it could not be computed", and a second spelling of it
 * is how one of those two paths comes to disagree with the breakdown it writes.
 */
export function namesTwoQuestions(questions: NominationAgePreferenceQuestions): boolean {
  return questions.minKey !== questions.maxKey;
}
